import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isValueEnabled } from "../lib/features";
import api from "../lib/api";
import { TeamBadge } from "../components/TeamBadge";
import { CapsuleRing } from "../components/CapsuleRing";
import { IndexGauge } from "../components/IndexGauge";
import { kickoffTime, kickoffDate, dateLocale, tzLabel } from "../lib/datetime";
import { Heatmap } from "../components/Heatmap";
import { ShareCard } from "../components/ShareCard";
import { Disclaimer } from "../components/Disclaimer";
import { ProGate, isPro } from "../components/ProGate";
import { FollowBell } from "../components/FollowBell";
import { LanguageSelector } from "../components/LanguageSelector";
import { useAuth } from "../context/AuthContext";
import { bandColor, outcomeColor } from "../lib/perf";
import api2 from "../lib/api";
import { useNavigate as useNav } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { isNative, platform } from "../lib/native";
import { loadMatchUnlockProduct, buyMatchUnlock as buyNativeMatchUnlock } from "../lib/iap";
import {
  CaretLeft, ShareNetwork, Star, Sparkle, Info, MapPin, Target, ListChecks, Scales, Crown, CheckCircle, ChartLineUp, CaretRight, LockKey, CaretDown,
} from "@phosphor-icons/react";

const Card = ({ title, children, action, testid }) => (
  <section className="hm-card p-4 mt-4 fade-up" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-white/50">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

const Skel = ({ className }) => <div className={`skeleton ${className}`} />;

// Squelette qui préfigure la forme de la fiche match (hero + indices + sections)
// pour le tout premier chargement, avant mise en cache.
const MatchSheetSkeleton = () => (
  <div className="grain relative min-h-screen" data-testid="matchsheet-skeleton">
    <div className="px-5 pt-5 pb-6">
      <div className="flex items-center justify-between">
        <Skel className="w-9 h-9 rounded-full" />
        <div className="flex gap-2"><Skel className="w-9 h-9 rounded-full" /><Skel className="w-9 h-9 rounded-full" /></div>
      </div>
      <div className="flex justify-center mt-5"><Skel className="h-3 w-40 rounded" /></div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-3 mt-4">
        <div className="flex flex-col items-center gap-2"><Skel className="w-16 h-16 rounded-full" /><Skel className="h-4 w-24 rounded" /></div>
        <div className="flex flex-col items-center gap-2 pt-2"><Skel className="h-8 w-16 rounded" /><Skel className="h-3 w-24 rounded" /></div>
        <div className="flex flex-col items-center gap-2"><Skel className="w-16 h-16 rounded-full" /><Skel className="h-4 w-24 rounded" /></div>
      </div>
      <div className="flex justify-center mt-4"><Skel className="h-3 w-28 rounded" /></div>
    </div>
    <div className="px-5 grid grid-cols-3 gap-3">
      <Skel className="h-24 rounded-2xl" /><Skel className="h-24 rounded-2xl" /><Skel className="h-24 rounded-2xl" />
    </div>
    <div className="px-5 mt-4 space-y-4">
      <Skel className="h-14 rounded-2xl" />
      <Skel className="h-44 rounded-2xl" />
      <Skel className="h-32 rounded-2xl" />
    </div>
  </div>
);


const CompareRow = ({ row }) => {
  const total = (row.home + row.away) || 1;
  const hp = (row.home / total) * 100;
  return (
    <div className="py-2">
      <div className="flex justify-between text-sm font-numeral font-bold mb-1">
        <span className={row.home_better ? "text-gold" : "text-white/70"}>{row.home}</span>
        <span className="text-[11px] font-sans font-medium text-white/40 self-center">{row.label}</span>
        <span className={!row.home_better ? "text-gold" : "text-white/70"}>{row.away}</span>
      </div>
      <div className="flex h-1.5 rounded-full overflow-hidden bg-white/5">
        <div style={{ width: `${hp}%`, background: "#D4AF37" }} />
        <div style={{ width: `${100 - hp}%`, background: "#2FAEE0" }} />
      </div>
    </div>
  );
};

const ValueFinderCard = ({ user, matchId, unlocked, onUnlock, unlockBusy, unlockPrice }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const hasAccess = isPro(user) || unlocked;
  useEffect(() => {
    if (!hasAccess || !matchId) return;
    setLoading(true);
    api.get(`/matches/${matchId}/value-finder`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [hasAccess, matchId]);

  const Head = (
    <div className="flex items-center gap-2 mb-2">
      <Scales size={18} weight="fill" color="#D4AF37" />
      <span className="text-xs font-bold uppercase tracking-[0.16em] gold-text">{t("matchsheet.vf_title")}</span>
    </div>
  );

  const Pending = (
    <div className="rounded-xl p-4 border border-gold/30" style={{ background: "linear-gradient(180deg, rgba(212,175,55,0.07), rgba(18,18,18,1))" }}>
      {Head}
      <p className="text-sm text-white/75 leading-relaxed">
        {t("matchsheet.vf_desc")}
      </p>
      <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 text-[11px] font-semibold text-white/55">
        <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
        {loading ? t("matchsheet.vf_loading") :
          data && data.available === false && data.key_configured === false
            ? t("matchsheet.vf_integrating")
            : t("matchsheet.vf_unavailable")}
      </div>
    </div>
  );

  const Live = data && data.available && (
    <div className="rounded-xl p-4 border" style={{
      background: data.value_detected
        ? "linear-gradient(180deg, rgba(0,230,118,0.10), rgba(18,18,18,1))"
        : "linear-gradient(180deg, rgba(212,175,55,0.07), rgba(18,18,18,1))",
      borderColor: data.value_detected ? "rgba(0,230,118,0.4)" : "rgba(212,175,55,0.3)",
    }}>
      {Head}
      {data.value_detected ? (
        <p className="text-sm text-white/85 leading-relaxed">
          <span className="text-perf-excellent font-bold">{t("matchsheet.vf_detected_label")}</span>
          {t("matchsheet.vf_detected_rest", { label: data.best.label, model: data.best.model_pct, implied: data.best.implied_pct })}
          <span className="text-perf-excellent font-bold">{t("matchsheet.vf_edge", { edge: data.best.edge })}</span>.
        </p>
      ) : (
        <p className="text-sm text-white/75 leading-relaxed">
          {t("matchsheet.vf_no_value", { label: data.best.label, edge: `${data.best.edge >= 0 ? "+" : ""}${data.best.edge}` })}
        </p>
      )}
      <div className="grid grid-cols-3 gap-2 mt-4">
        {["home", "draw", "away"].map((k) => (
          <div key={k} className="rounded-lg bg-white/5 p-2 text-center" style={{ borderTop: `2px solid ${outcomeColor(k)}` }}>
            <p className="text-[10px] truncate" style={{ color: outcomeColor(k) }}>{data.labels[k]}</p>
            <p className="text-xs mt-1"><span className="text-white/50">{t("matchsheet.vf_model")}</span> <span className="font-bold">{data.model[k]}%</span></p>
            <p className="text-xs"><span className="text-white/50">{t("matchsheet.vf_market")}</span> <span className="font-bold">{data.market[k]}%</span></p>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-white/35 mt-3">
        {t("matchsheet.vf_source", { source: data.source, count: data.bookmakers })}
      </p>
    </div>
  );

  const body = Live || Pending;
  return (
    <section className="hm-card p-4 mt-4 fade-up" data-testid="value-finder-card">
      {hasAccess ? body : (
        <>
          <ProGate user={user} testid="value-finder-gate" to="/paywall"
            note={t("matchsheet.vf_gate_note")}>
            {Pending}
          </ProGate>
          {!isPro(user) && (
            <button onClick={onUnlock} disabled={unlockBusy}
              className="w-full mt-2 text-center text-[12px] text-white/40 hover:text-white/60 disabled:opacity-60 py-1">
              {unlockBusy ? "Redirection…" : `ou débloquer juste ce match — ${typeof unlockPrice === "string" ? unlockPrice : `${(unlockPrice ?? 1.99).toFixed(2).replace(".", ",")}€`}`}
            </button>
          )}
        </>
      )}
    </section>
  );
};

export default function MatchSheet() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const [m, setM] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [pred, setPred] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [explain, setExplain] = useState(false);
  const [share, setShare] = useState(null);
  const [h2hFilter, setH2hFilter] = useState(5);
  const [statsOpen, setStatsOpen] = useState(false);
  const [predResult, setPredResult] = useState(null); // {finished, correct, shareable, score, ...}
  const [highConf, setHighConf] = useState(null); // {sample, accuracy_pct}
  const [trial, setTrial] = useState(null); // {free_trial_used, unlocked, free_trial_match_id}
  const [subFrom, setSubFrom] = useState(6.99);
  const [valueCount, setValueCount] = useState(null);

  // Changement de match : on vide immédiatement les données du match précédent
  // et on ignore toute réponse arrivée après coup pour un autre id.
  const idRef = useRef(id);
  idRef.current = id;
  const [shownId, setShownId] = useState(id);
  if (shownId !== id) {
    setShownId(id);
    setM(null); setLoadErr(false); setPred(null); setAi(null); setAiLoading(false);
    setShare(null); setPredResult(null); setTrial(null); setValueCount(null);
  }

  useEffect(() => {
    const reqId = id;
    api.get(`/matches/${id}`).then((r) => { if (idRef.current === reqId) setM(r.data); }).catch(() => {
      if (idRef.current !== reqId) return;
      const fp = location.state?.pred;
      if (fp?.home?.name && fp?.away?.name) {
        const sp = fp.sport || "foot";
        navigate(`/prediction/${sp}/${encodeURIComponent(fp.home.name)}/${encodeURIComponent(fp.away.name)}`,
          { replace: true, state: { pred: fp } });
      } else {
        setLoadErr(true);
      }
    });
    // Un chargement par match : location.state/navigate changent à la redirection et relanceraient la demande.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Le partage d'image du pronostic n'est autorisé QUE si le match est terminé
  // ET le pronostic s'est révélé juste (jamais avant le résultat, jamais si faux).
  useEffect(() => {
    const reqId = id;
    api.get(`/matches/${id}/prediction-result`)
      .then((r) => { if (idRef.current === reqId) setPredResult(r.data); })
      .catch(() => { if (idRef.current === reqId) setPredResult(null); });
  }, [id]);

  // Précision historique de la bande "confiance élevée" pour le badge du pronostic.
  useEffect(() => {
    api.get("/performance/high-confidence")
      .then((r) => setHighConf(r.data))
      .catch(() => setHighConf(null));
  }, []);

  // Navigation fluide entre matchs : liste ordonnée des matchs pour aller au
  // précédent/suivant sans revenir à la liste (flèches + balayage).
  const [siblings, setSiblings] = useState([]);
  const swipeRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    api.get("/live/matches")
      .then(({ data }) => setSiblings((data.matches || []).map((mm) => `fx_${mm.match_id}`)))
      .catch(() => setSiblings([]));
  }, []);
  const sibIdx = siblings.indexOf(id);
  const prevId = sibIdx > 0 ? siblings[sibIdx - 1] : null;
  const nextId = sibIdx >= 0 && sibIdx < siblings.length - 1 ? siblings[sibIdx + 1] : null;
  const goSibling = (sid) => { if (sid) navigate(`/match/${sid}`); };
  const onTouchStart = (e) => { const tt = e.touches[0]; swipeRef.current = { x: tt.clientX, y: tt.clientY }; };
  const onTouchEnd = (e) => {
    const tt = e.changedTouches[0];
    const dx = tt.clientX - swipeRef.current.x;
    const dy = tt.clientY - swipeRef.current.y;
    if (Math.abs(dx) > 80 && Math.abs(dy) < 50) goSibling(dx < 0 ? nextId : prevId);
  };

  // Mémorise les 3 derniers matchs consultés (accès rapide sous la recherche).
  useEffect(() => {
    if (!m) return;
    try {
      const key = "hitmax_recent_matches";
      const light = (s) => ({ team_id: s.team_id, name: s.name, short: s.short, crest: s.crest, color: s.color });
      const entry = { match_id: id, home: light(m.home), away: light(m.away), competition: m.competition };
      const cur = JSON.parse(localStorage.getItem(key) || "[]");
      const next = [entry, ...cur.filter((x) => x.match_id !== id)].slice(0, 3);
      localStorage.setItem(key, JSON.stringify(next));
    } catch { /* ignore */ }
  }, [m, id]);

  // Pre-check the free-trial status so the button label is right (no dead clicks).
  useEffect(() => {
    const reqId = id;
    api.get("/user/free-trial-status", { params: { match_id: id } })
      .then((r) => { if (idRef.current === reqId) setTrial(r.data); })
      .catch(() => { if (idRef.current === reqId) setTrial({ free_trial_used: false, unlocked: false, match_unlocked: false }); });
  }, [id]);

  // free user, trial spent, this match not already consumed AND not bought individually -> locked
  // "Locked" = tout non-payant (y compris non connecté) qui n'a pas débloqué CE
  // match : il voit l'aperçu (probas en clair + premium flouté + CTA).
  const isLocked = !!(trial && !trial.unlocked && !trial.match_unlocked && String(trial.free_trial_match_id) !== String(id));
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [appleUnlockPrice, setAppleUnlockPrice] = useState(null);
  useEffect(() => {
    if (!isNative() || platform() !== "ios") return;
    let active = true;
    loadMatchUnlockProduct().then((product) => {
      if (active) setAppleUnlockPrice(product?.priceString || "Indisponible");
    }).catch(() => { if (active) setAppleUnlockPrice("Indisponible"); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const delivered = () => {
      const matchId = id;
      api.get("/user/free-trial-status", { params: { match_id: matchId } }).then(({ data }) => {
        if (idRef.current === matchId) setTrial(data);
      }).catch(() => {});
    };
    window.addEventListener("hitmax-iap-delivered", delivered);
    return () => window.removeEventListener("hitmax-iap-delivered", delivered);
  }, [id]);

  // Révélation au déblocage : anime le flou qui se dissipe (~0.7s) quand l'accès
  // vient d'être accordé — soit au retour du paiement (?revealed=1), soit quand
  // l'état passe de verrouillé à débloqué pendant la session.
  const [searchParams] = useSearchParams();
  const [revealActive, setRevealActive] = useState(false);
  const prevLockedRef = useRef(null);
  const revealFiredRef = useRef(false);
  const triggerReveal = () => {
    setRevealActive(true);
    setTimeout(() => setRevealActive(false), 750);
  };
  useEffect(() => {
    if (revealFiredRef.current) return;
    // Cas 1 : retour d'un paiement réussi (redirigé vers cette fiche).
    if (searchParams.get("revealed") === "1" && trial && !isLocked) {
      revealFiredRef.current = true;
      triggerReveal();
      return;
    }
    // Cas 2 : transition verrouillé -> débloqué en cours de session.
    if (prevLockedRef.current === true && isLocked === false) {
      revealFiredRef.current = true;
      triggerReveal();
    }
    if (trial) prevLockedRef.current = isLocked;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked, trial, searchParams]);

  // Non-abonné / non-débloqueur : charger l'aperçu (probabilités réelles) pour
  // afficher une vraie preuve de qualité en clair, le premium restant flouté.
  useEffect(() => {
    if (isLocked && !ai) {
      const reqId = id;
      api.get(`/matches/${id}/preview`)
        .then(({ data }) => {
          if (idRef.current !== reqId) return;
          setPred((cur) => (cur && !cur._preview ? cur : { ...data.prediction, _preview: true }));
          if (data.subscribe_from_price) setSubFrom(data.subscribe_from_price);
          if (typeof data.value_count === "number") setValueCount(data.value_count);
        })
        .catch(() => {});
    }
    // `ai` change après ce chargement : l'ajouter relancerait la demande d'aperçu (une seule voulue).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked, id]);

  const buyMatchUnlock = async () => {
    setUnlockBusy(true);
    try {
      if (isNative() && ["ios", "android"].includes(platform())) {
        await buyNativeMatchUnlock(id);
        if (idRef.current === id) setTrial((current) => ({ ...(current || {}), match_unlocked: true }));
        toast.success("Match débloqué");
        setUnlockBusy(false);
        return;
      }
      throw new Error("L’achat à l’unité est disponible dans l’application mobile");
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || "Impossible d'ouvrir le paiement. Réessaie.");
      setUnlockBusy(false);
    }
  };

  const runAnalysis = async () => {
    if (isLocked) return; // le choix se fait désormais sur place (voir bloc ci-dessous), plus de redirection sèche
    setAiLoading(true);
    const reqId = id;
    try {
      const { data } = await api.post(`/matches/${id}/ai-analysis`);
      if (idRef.current !== reqId) return;
      if (data.status === "paywall") {
        setTrial((t) => ({ ...(t || {}), free_trial_used: true }));
      } else {
        setAi(data.analysis.summary);
        setPred(data.analysis.prediction);
        setTrial((t) => ({ ...(t || {}), free_trial_used: true, free_trial_match_id: data.replay ? t?.free_trial_match_id : id }));
        if (data.status === "free_trial" && !data.replay) { toast.success(t("matchsheet.ai_unlocked")); refreshUser().catch(() => {}); }
      }
    } catch { if (idRef.current === reqId) toast.error(t("matchsheet.ai_unavailable")); }
    if (idRef.current === reqId) setAiLoading(false);
  };

  // Rechargement live à la bascule de langue : on rafraîchit les textes venant
  // du backend (labels, raisonnement, résumé IA) sans revenir en arrière.
  // Sûr : les payants/admin rechargent l'analyse complète (gratuit), les
  // verrouillés rechargent l'aperçu, l'essai gratuit n'est jamais reconsommé.
  useEffect(() => {
    const onLang = () => {
      const reqId = id;
      api.get(`/matches/${id}`).then((r) => { if (idRef.current === reqId) setM(r.data); }).catch(() => {});
      if (isLocked) {
        api.get(`/matches/${id}/preview`)
          .then(({ data }) => { if (idRef.current === reqId) setPred({ ...data.prediction, _preview: true }); })
          .catch(() => {});
      } else if (ai && (trial?.unlocked || trial?.match_unlocked)) {
        runAnalysis();
      }
    };
    i18n.on("languageChanged", onLang);
    return () => i18n.off("languageChanged", onLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isLocked, ai, trial]);


  const isFav = (user?.favorites || []).some((f) => f.kind === "team" && (f.ref_id === m?.home.team_id));
  const toggleFav = async () => {
    if (!m) return;
    await api2.post("/favorites/toggle", { kind: "team", ref_id: m.home.team_id });
    await refreshUser();
    toast.success(isFav ? t("matchsheet.fav_removed") : t("matchsheet.fav_added"));
  };

  if (loadErr) return (
    <div className="min-h-screen grain flex flex-col items-center justify-center gap-4 px-8 text-center" data-testid="matchsheet-error">
      <p className="text-white/70 text-sm">{t("matchsheet.not_found")}</p>
      <button data-testid="matchsheet-error-back" onClick={() => navigate(-1)}
        className="px-5 py-2.5 rounded-full bg-gold text-black text-sm font-bold">{t("common.back")}</button>
    </div>
  );
  if (!m) return <MatchSheetSkeleton />;

  const teamName = (s) => (s === m.home.short ? m.home.name : s === m.away.short ? m.away.name : s);

  return (
    <div className={`grain relative ${revealActive ? "reveal-unblur" : ""}`} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {prevId && (
        <button data-testid="match-prev-btn" aria-label={t("matchsheet.prev_match")} onClick={() => goSibling(prevId)}
          className="fixed left-1 top-1/2 -translate-y-1/2 z-40 w-9 h-9 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors">
          <CaretLeft size={18} weight="bold" />
        </button>
      )}
      {nextId && (
        <button data-testid="match-next-btn" aria-label={t("matchsheet.next_match")} onClick={() => goSibling(nextId)}
          className="fixed right-1 top-1/2 -translate-y-1/2 z-40 w-9 h-9 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors">
          <CaretRight size={18} weight="bold" />
        </button>
      )}
      {/* Hero */}
      <div className="relative">
        <img src={m.background} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0A0A0A 8%, rgba(10,10,10,0.8) 50%, rgba(10,10,10,0.5))" }} />
        <div className="relative z-10 px-5 pt-5 pb-6">
          <div className="flex items-center justify-between">
            <button data-testid="back-btn" onClick={() => navigate(-1)} className="w-9 h-9 rounded-full glass flex items-center justify-center">
              <CaretLeft size={18} weight="bold" />
            </button>
            <div className="flex gap-2 items-center">
              <LanguageSelector variant="header" />
              <FollowBell match={{ match_id: id, home: m.home, away: m.away, competition: m.competition }} size={18} />
              <button data-testid="fav-btn" onClick={toggleFav} className="w-9 h-9 rounded-full glass flex items-center justify-center">
                <Star size={18} weight={isFav ? "fill" : "regular"} color={isFav ? "#D4AF37" : "#fff"} />
              </button>
              <button data-testid="share-match-btn" onClick={() => setShare({ title: `${m.home.name} vs ${m.away.name}`, bigValue: m.index.home.value, subtitle: t("matchsheet.share_index", { team: m.home.name }), band: m.index.home.band, context: `${m.competition} · ${m.venue}`, localize: (tt) => ({ subtitle: tt("matchsheet.share_index", { team: m.home.name }) }) })} className="w-9 h-9 rounded-full glass flex items-center justify-center">
                <ShareNetwork size={18} weight="bold" />
              </button>
            </div>
          </div>
          <p className="text-center text-[10px] font-bold uppercase tracking-[0.25em] text-gold mt-3 line-clamp-1">{m.competition}</p>
          <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-3 mt-4">
            <div className="flex flex-col items-center gap-2 min-w-0">
              <TeamBadge team={m.home} size={64} />
              <span className="font-bold text-center leading-tight text-sm line-clamp-2 w-full break-words" data-testid="match-home-name">{m.home.name}</span>
            </div>
            <div className="text-center pt-2 shrink-0">
              <p className="font-numeral font-black text-3xl whitespace-nowrap">{kickoffTime(m)}{tzLabel(m.kickoff ? new Date(m.kickoff) : undefined) ? <span className="text-sm text-white/50 ml-1">{tzLabel(m.kickoff ? new Date(m.kickoff) : undefined)}</span> : null}</p>
              <p className="text-[11px] text-white/50 whitespace-nowrap">{kickoffDate(m, { weekday: "short", day: "2-digit", month: "short" })}</p>
            </div>
            <div className="flex flex-col items-center gap-2 min-w-0">
              <TeamBadge team={m.away} size={64} />
              <span className="font-bold text-center leading-tight text-sm line-clamp-2 w-full break-words" data-testid="match-away-name">{m.away.name}</span>
            </div>
          </div>
          <p className="flex items-center justify-center gap-1 text-xs text-white/50 mt-3">
            <MapPin size={13} /> {m.venue}
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 -mt-2 relative z-10">
        {/* HITMAX AI */}
        <div className="hm-card p-4 mt-2 border-gold/25" style={{ boxShadow: "0 0 20px rgba(212,175,55,0.1)" }} data-testid="ai-summary">
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={18} weight="fill" color="#D4AF37" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] gold-text">{t("matchsheet.ai_header")}</span>
          </div>
          {ai ? (
            <p className="text-sm text-white/80 leading-relaxed">{ai}</p>
          ) : isLocked ? (
            <div data-testid="ai-locked-choice">
              <div className="relative">
                <div className="pointer-events-none select-none blur-[6px] opacity-40" aria-hidden="true">
                  <p className="text-sm text-white/80 leading-relaxed">
                    {"L'équipe à domicile présente un avantage statistique marqué. Efficacité offensive supérieure, dynamique récente favorable et solidité défensive orientent nettement notre modèle. Les indicateurs xG et le contexte de la rencontre confirment une tendance forte à exploiter…"}
                  </p>
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-3 text-center">
                  {trial?.upsell?.message && (
                    <div className="mb-1 rounded-lg px-3 py-2 text-left" data-testid="unlock-upsell"
                      style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.40)" }}>
                      <span className="text-[11px] text-gold leading-snug">{trial.upsell.message}</span>
                    </div>
                  )}
                  {valueCount > 0 && (
                    <div className="mb-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1" data-testid="value-count-teaser"
                      style={{ background: "rgba(0,230,118,0.15)", border: "1px solid rgba(0,230,118,0.45)" }}>
                      <span className="text-[11px] font-bold text-perf-excellent">
                        {`\u{1F50D} ${valueCount} opportunité${valueCount > 1 ? "s" : ""} value détectée${valueCount > 1 ? "s" : ""}`}
                      </span>
                    </div>
                  )}
                  <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5" data-testid="ai-locked-hint"
                    style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.4)" }}>
                    <LockKey size={13} weight="fill" color="#D4AF37" />
                    <span className="text-[11px] font-bold text-gold">{t("matchsheet.locked_hint")}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (() => {
            const label = aiLoading
              ? t("matchsheet.ai_analyzing")
              : t("matchsheet.ai_generate");
            return (
              <>
                <button data-testid="ai-analyse-btn" onClick={runAnalysis} disabled={aiLoading}
                  className="text-sm font-bold flex items-center gap-2 text-perf-excellent">
                  {label}
                </button>
              </>
            );
          })()}
        </div>

        {/* Partage pronostic confirmé — UNIQUEMENT si match terminé ET pronostic juste */}
        {predResult?.shareable && (
          <button
            data-testid="share-confirmed-btn"
            onClick={() => setShare({
              badge: t("matchsheet.confirmed_badge"),
              title: `${predResult.home} vs ${predResult.away}`,
              logos: [
                { crest: m.home.crest, name: m.home.name, short: m.home.short },
                { crest: m.away.crest, name: m.away.name, short: m.away.short },
              ],
              bigValue: predResult.score || "✓",
              subtitle: predResult.recommendation_text
                ? t("matchsheet.confirmed_reco", { reco: predResult.recommendation_text })
                : t("matchsheet.confirmed_sub"),
              band: "excellent",
              context: m.competition,
              shareText: t("matchsheet.share_won_text", { home: predResult.home, score: predResult.score || "", away: predResult.away, reco: predResult.recommendation_text || "" }),
              localize: (tt) => ({
                badge: tt("matchsheet.confirmed_badge"),
                subtitle: predResult.recommendation_text
                  ? tt("matchsheet.confirmed_reco", { reco: predResult.recommendation_text })
                  : tt("matchsheet.confirmed_sub"),
                shareText: tt("matchsheet.share_won_text", { home: predResult.home, score: predResult.score || "", away: predResult.away, reco: predResult.recommendation_text || "" }),
              }),
            })}
            className="w-full mt-4 rounded-2xl p-4 flex items-center justify-between gap-3 fade-up"
            style={{ background: "linear-gradient(160deg, rgba(0,230,118,0.14), rgba(18,18,18,1))", border: "1px solid rgba(0,230,118,0.45)" }}
          >
            <div className="flex items-center gap-3 min-w-0 text-left">
              <CheckCircle size={26} weight="fill" color="#00E676" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-perf-excellent leading-tight">{t("matchsheet.confirmed_badge")}</p>
                <p className="text-[11px] text-white/55 truncate">
                  {predResult.score ? `${predResult.score} · ` : ""}{predResult.recommendation_text}
                </p>
              </div>
            </div>
            <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-perf-excellent text-black text-xs font-bold">
              <ShareNetwork size={15} weight="bold" /> {t("matchsheet.confirmed_share")}
            </span>
          </button>
        )}

        {/* HITMAX Prediction */}
        {pred && (
          <Card
            title={t("matchsheet.pred_title")}
            testid="prediction-card"
            action={<span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: bandColor(pred.band) }}>{pred.label}</span>}
          >
            <div className="flex flex-col items-center">
              <IndexGauge value={pred.hitmax_score} band={pred.band} size={168} label={t("matchsheet.hitmax_score")} />
            </div>

            <div className="mt-2 flex items-center justify-between gap-2 rounded-xl p-3"
              style={{ background: `${bandColor(pred.band)}14`, border: `1px solid ${bandColor(pred.band)}55` }}
              data-testid="prediction-reco">
              <div className="flex items-center gap-2 min-w-0">
                <Target size={18} weight="fill" color={bandColor(pred.band)} />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-white/50">{t("matchsheet.reco")}</p>
                  <p className="font-bold text-sm break-words">{pred.recommendation.text}</p>
                </div>
              </div>
              <span className="font-numeral font-black text-2xl" style={{ color: bandColor(pred.band) }}>{pred.recommendation.prob}%</span>
            </div>

            {pred.band === "excellent" && highConf && highConf.sample >= 10 && highConf.accuracy_pct != null && (
              <div role="button" tabIndex={0} data-testid="hc-track-badge"
                onClick={() => navigate("/performance")}
                onKeyDown={(e) => { if (e.key === "Enter") navigate("/performance"); }}
                className="mt-2 flex items-center gap-2 rounded-xl px-3 py-2 cursor-pointer"
                style={{ background: "rgba(0,230,118,0.10)", border: "1px solid rgba(0,230,118,0.4)" }}>
                <ChartLineUp size={16} weight="fill" color="#00E676" className="shrink-0" />
                <p className="text-[11px] leading-snug text-white/70">
                  <span className="font-bold text-perf-excellent">{t("matchsheet.hc_badge_title")}</span>
                  {" · "}
                  {t("matchsheet.hc_badge_stat", { pct: highConf.accuracy_pct })}
                </p>
                <CaretRight size={13} weight="bold" color="#00E676" className="ml-auto shrink-0" />
              </div>
            )}

            <div className="mt-2 flex items-center justify-between text-[11px] text-white/50">
              {pred.alt ? (
                <span>{t("matchsheet.alt")}<span className="text-white/80 font-semibold">{pred.alt.text}</span>{pred.alt.prob != null ? ` (${pred.alt.prob}%)` : ""}</span>
              ) : <span />}
              {pred._preview ? (
                <span>{t("matchsheet.likely_score")} <span className="font-numeral text-gold blur-[5px] select-none" aria-hidden="true">2-1</span></span>
              ) : pred.likely_score ? (
                <span>{t("matchsheet.likely_score")} <span className="font-numeral text-gold">{pred.likely_score}</span>{pred.likely_score_prob != null ? ` (${pred.likely_score_prob}%)` : ""}</span>
              ) : <span />}
            </div>

            <div className="mt-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-2">{t("matchsheet.scenarios")}</p>
              <div className="space-y-2">
                {pred.scenarios.map((s) => (
                  <div key={s.key} className="flex items-center gap-2">
                    <span className="min-w-[3rem] max-w-[45%] text-xs font-semibold text-white/70 break-words leading-tight">{s.label}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${s.pct}%`, background: outcomeColor(s.key) }} />
                    </div>
                    <span className="w-9 text-right font-numeral font-bold text-sm">{s.pct}%</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4" data-testid="odds-section">
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-2" data-testid="odds-section-title">{pred.source === "live" ? t("matchsheet.market_odds") : t("matchsheet.model_odds_title")}</p>
              {pred.odds && pred.odds.home ? (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    {[["1", "home", pred.odds.home], ["N", "draw", pred.odds.draw], ["2", "away", pred.odds.away]].map(([l, k, o]) => (
                      <div key={l} className="bg-white/5 rounded-lg p-2 text-center" style={{ borderTop: `2px solid ${outcomeColor(k)}` }}>
                        <p className="text-[10px]" style={{ color: outcomeColor(k) }}>{l}</p>
                        <p className="font-numeral font-bold" style={{ color: outcomeColor(k) }}>{o}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-white/40 mt-1 text-center">
                    {pred.source === "live" ? t("matchsheet.odds_api") : t("matchsheet.odds_model")}
                    {isValueEnabled() && pred.market_edge != null ? t("matchsheet.edge_suffix", { edge: pred.market_edge }) : ""}
                  </p>
                </>
              ) : (
                <div className="bg-white/5 rounded-lg p-3 text-center" data-testid="odds-unavailable">
                  <p className="text-[11px] text-white/40">{t("matchsheet.odds_unavailable")}</p>
                </div>
              )}
            </div>

            <div className="mt-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-2 flex items-center gap-1">
                <ListChecks size={13} weight="bold" /> {t("matchsheet.why")}
              </p>
              <ProGate user={user} testid="reasoning-gate" to="/paywall"
                note={t("matchsheet.reasoning_gate")}>
                <ul className="space-y-1.5" data-testid="prediction-reasoning">
                  {(pred._preview
                    ? ["Analyse xG détaillée des deux équipes", "Impact des absences et de la forme récente", "Historique des confrontations directes", "Signaux détectés sur le marché des cotes"]
                    : (pred.reasoning || [])).map((r, i) => (
                    <li key={i} className="flex gap-2 text-xs text-white/70">
                      <span className="text-gold mt-0.5">▸</span><span>{r}</span>
                    </li>
                  ))}
                </ul>
              </ProGate>
            </div>

            <Disclaimer className="mt-4" />
          </Card>
        )}

        {isValueEnabled() && (
          <ValueFinderCard key={id} user={user} matchId={id} unlocked={!!trial?.match_unlocked}
            onUnlock={buyMatchUnlock} unlockBusy={unlockBusy} unlockPrice={isNative() && platform() === "ios" ? (appleUnlockPrice || "Chargement…") : trial?.match_unlock_price} />
        )}

        {m.top_scorers && ((m.top_scorers.home && m.top_scorers.home.length) || (m.top_scorers.away && m.top_scorers.away.length)) ? (
          <Card testid="top-scorers-section" title="">
            <p className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-3 flex items-center gap-1">
              <Target size={13} weight="bold" /> {t("matchsheet.top_scorers")}
            </p>
            <div className="grid grid-cols-2 gap-4">
              {["home", "away"].map((side) => (
                <div key={side}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-xs font-bold" style={{ color: m[side].color }}>{m[side].short || m[side].name}</span>
                  </div>
                  {(m.top_scorers[side] || []).length ? (m.top_scorers[side]).map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-b border-white/5 last:border-0" data-testid={`scorer-${side}-${i}`}>
                      <span className="text-[12px] text-white/75 truncate mr-2">{p.name}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {p.goal_odds ? (
                          <span className="text-[10px] font-numeral font-bold text-gold bg-gold/10 border border-gold/25 rounded px-1.5 py-0.5 whitespace-nowrap" data-testid={`scorer-${side}-${i}-odds`} title={t("matchsheet.scorer_odds_hint")}>
                            {t("matchsheet.scorer_odds_label")} @{Number(p.goal_odds).toFixed(2)}
                          </span>
                        ) : null}
                        <span className="text-[12px] font-numeral font-bold text-gold">{p.stats?.goals ?? 0}<span className="text-[9px] text-white/40 ml-0.5">{t("matchsheet.goals_short")}</span></span>
                      </div>
                    </div>
                  )) : <p className="text-[11px] text-white/30">—</p>}
                </div>
              ))}
            </div>
            {m.scorer_odds_available === false ? (
              <p className="text-[10px] text-white/30 mt-3" data-testid="scorer-odds-unavailable">{t("matchsheet.scorer_odds_unavailable")}</p>
            ) : null}
            {String(m.competition_id || "").startsWith("af_") && (
              <button
                data-testid="see-full-scorers"
                onClick={() => navigate(`/buteurs?comp=${m.competition_id}`)}
                className="mt-3 w-full flex items-center justify-center gap-1.5 text-[11px] font-bold text-gold hover:opacity-80 transition-opacity">
                <Target size={12} weight="bold" /> {t("matchsheet.see_full_scorers")}
              </button>
            )}
          </Card>
        ) : null}

        {/* HITMAX Index */}
        <Card title={t("matchsheet.index_title")} testid="index-card"
          action={<button data-testid="explain-btn" onClick={() => setExplain(true)} className="flex items-center gap-1 text-[11px] font-bold text-gold"><Info size={13} weight="bold" /> {t("matchsheet.explain")}</button>}>
          <div className="grid grid-cols-2 gap-2">
            {["home", "away"].map((side) => {
              const idx = m.index[side];
              return (
                <div key={side} className="flex flex-col items-center">
                  <span className="text-xs font-bold text-white/60 mb-1 text-center leading-tight">{m[side].name}</span>
                  <IndexGauge value={idx.value} band={idx.band} size={140} label="" />
                </div>
              );
            })}
          </div>
        </Card>

        {/* Capsules HITMAX */}
        <Card title={t("matchsheet.capsules_title")} testid="capsules-card"
          action={<button data-testid="share-capsules" onClick={() => { const c = m.capsules.find(x => x.label === "+2.5"); setShare({ title: t("matchsheet.capsule_share_title"), bigValue: c.pct, unit: "%", subtitle: `${m.home.name} vs ${m.away.name}`, band: c.band, context: m.competition, localize: (tt) => ({ title: tt("matchsheet.capsule_share_title") }) }); }}><ShareNetwork size={15} color="#D4AF37" /></button>}>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
            {m.capsules.map((c) => <CapsuleRing key={c.label} pct={c.pct} label={c.label} band={c.band} size={96} />)}
          </div>
        </Card>

        {/* Statistiques détaillées — repliées par défaut pour alléger l'écran */}
        <button data-testid="toggle-more-stats" onClick={() => setStatsOpen((v) => !v)}
          className="w-full mt-2 mb-1 h-11 rounded-xl flex items-center justify-center gap-2 text-sm font-bold text-white/70 hover:text-white transition-colors"
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
          {statsOpen ? "Masquer les statistiques détaillées" : "Voir plus de statistiques"}
          <CaretDown size={16} weight="bold" style={{ transform: statsOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
        </button>
        {statsOpen && (<>
        {/* Goal distribution */}
        <Card title={t("matchsheet.goal_dist_title")} testid="goal-dist-card">
          <div className="flex items-end justify-between gap-2 h-32">
            {m.goal_distribution.map((g) => (
              <div key={g.goals} className="flex-1 flex flex-col items-center gap-1.5">
                <span className="text-[11px] font-numeral font-bold text-gold">{g.pct}%</span>
                <div className="w-full rounded-t-md bg-gradient-to-t from-gold-dark to-gold" style={{ height: `${Math.max(6, g.pct * 1.7)}px` }} />
                <span className="text-[10px] font-semibold text-white/50">{g.goals}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Stadium */}
        <Card title={t("matchsheet.stadium_title")} testid="stadium-card">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-sm font-semibold">{m.stadium.name}</p>
              <p className="text-xs text-white/40 mt-0.5">{t("matchsheet.capacity", { n: m.stadium.capacity.toLocaleString(dateLocale()) })}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-white/40">{t("matchsheet.home_at_venue", { team: m.home.name })}</p>
              <p className="font-numeral font-bold text-sm mt-0.5">
                <span className="text-perf-excellent">{m.stadium.home_at_venue.won}V</span>{" "}
                <span className="text-perf-warning">{m.stadium.home_at_venue.drawn}N</span>{" "}
                <span className="text-perf-danger">{m.stadium.home_at_venue.lost}D</span>
              </p>
            </div>
          </div>
        </Card>

        {/* Standings snapshot */}
        <Card title={t("matchsheet.standings_title")} testid="standings-snap">
          <div className="grid grid-cols-2 gap-3">
            {["home", "away"].map((side) => (
              <div key={side} className="bg-white/5 rounded-xl p-3 text-center">
                <p className="text-xs text-white/50">{m[side].name}</p>
                <p className="font-numeral font-black text-2xl text-gold">{m.standings[side].rank}<span className="text-sm text-white/40">{t("matchsheet.rank_suffix")}</span></p>
                <p className="text-[11px] text-white/50">{t("matchsheet.pts", { n: m.standings[side].pts })}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Comparison */}
        <Card title={t("matchsheet.comparison_title")} testid="comparison-card">
          <div className="flex justify-between text-xs font-bold mb-1">
            <span className="text-gold">{m.home.name}</span>
            <span className="text-[#2FAEE0]">{m.away.name}</span>
          </div>
          {m.comparison.map((row, i) => <CompareRow key={i} row={row} />)}
        </Card>

        {/* Form */}
        <Card title={t("matchsheet.form_title")} testid="form-card">
          {["home", "away"].map((side) => {
            const f = m.form[side];
            return (
              <div key={side} className="flex items-center gap-3 py-1.5">
                <span className="w-24 text-xs font-bold leading-tight">{m[side].name}</span>
                <div className="flex gap-1">
                  {f.form.slice(0, 10).map((r, i) => (
                    <span key={i} className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold text-black"
                      style={{ background: r === "W" ? "#00E676" : r === "D" ? "#FB8C00" : "#E53935" }}>{r}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </Card>

        {/* H2H */}
        <Card title={t("matchsheet.h2h_title")} testid="h2h-card"
          action={
            <div className="flex gap-1">
              {[5, 10, 20].map((n) => (
                <button key={n} data-testid={`h2h-filter-${n}`} onClick={() => setH2hFilter(n)}
                  className={`px-2 py-0.5 rounded text-[10px] font-bold ${h2hFilter === n ? "bg-gold text-black" : "bg-white/5 text-white/50"}`}>{n}</button>
              ))}
            </div>
          }>
          {m.h2h.slice(0, h2hFilter).map((h, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
              <span className="text-xs text-white/40 w-20">{h.date}</span>
              <span className="text-sm font-semibold flex-1 text-center">{teamName(h.home)} <span className="font-numeral text-gold mx-1">{h.score}</span> {teamName(h.away)}</span>
              <span className="text-[10px] font-bold w-20 text-right leading-tight" style={{ color: h.winner ? "#00E676" : "#FB8C00" }}>{h.winner ? teamName(h.winner) : t("matchsheet.draw")}</span>
            </div>
          ))}
        </Card>

        {/* Heatmap */}
        {m.heatmap && (
          <Card title={t("matchsheet.heatmap_title")} testid="heatmap-card">
            <Heatmap intervals={m.heatmap.intervals} home={m.heatmap.home} away={m.heatmap.away}
              homeShort={m.home.short} awayShort={m.away.short} />
          </Card>
        )}

        {/* Absences */}
        {m.absences?.length > 0 && (
        <Card title={t("matchsheet.absences_title")} testid="absences-card">
          <div className="space-y-2">
            {m.absences.map((a, i) => {
              const c = a.status === "out" ? "#E53935" : a.status === "doubt" ? "#FB8C00" : "#00E676";
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full" style={{ background: c }} />
                  <span className="text-sm font-medium flex-1">{a.player}</span>
                  <span className="text-[11px] text-white/40">{teamName(a.team)}</span>
                  <span className="text-[11px] font-semibold w-24 text-right" style={{ color: c }}>{a.reason}</span>
                </div>
              );
            })}
          </div>
        </Card>
        )}

        {/* Referee */}
        {m.referee?.name && (
        <Card title={t("matchsheet.referee_title")} testid="referee-card">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">{m.referee.name}</span>
            <div className="flex gap-4 text-center">
              <div><p className="font-numeral font-bold text-gold">{m.referee.yellow_avg}</p><p className="text-[9px] text-white/40">{t("matchsheet.ref_yellow")}</p></div>
              <div><p className="font-numeral font-bold text-gold">{m.referee.penalties}</p><p className="text-[9px] text-white/40">{t("matchsheet.ref_pen")}</p></div>
              <div><p className="font-numeral font-bold text-gold">{m.referee.matches}</p><p className="text-[9px] text-white/40">{t("matchsheet.ref_matches")}</p></div>
            </div>
          </div>
        </Card>
        )}
        </>)}

        <div className={isLocked ? "h-44" : "h-8"} />
      </div>

      {/* Sticky bottom CTA for locked sheets (above the bottom nav) */}
      {isLocked && createPortal(
        <div data-testid="sticky-unlock-bar" className="fixed inset-x-0 z-40 pointer-events-none"
          style={{ bottom: "calc(66px + env(safe-area-inset-bottom, 0px))" }}>
          <div className="max-w-2xl mx-auto px-4 pointer-events-auto">
            <div className="rounded-2xl p-2.5 flex items-center gap-2.5"
              style={{ background: "rgba(13,13,13,0.97)", border: "1px solid rgba(212,175,55,0.45)", boxShadow: "0 -6px 24px rgba(0,0,0,0.55)", backdropFilter: "blur(12px)" }}>
              <button data-testid="sticky-subscribe-btn" onClick={() => navigate("/paywall")}
                className="flex-1 h-12 rounded-xl font-bold text-sm bg-gold text-black flex flex-col items-center justify-center leading-tight">
                <span>{"S'abonner"}</span>
                <span className="text-[10px] font-medium opacity-70">{t("matchsheet.sticky_sub_hint", { price: Number(subFrom).toFixed(2).replace(".", ",") })}</span>
              </button>
              <button data-testid="sticky-unlock-btn" onClick={buyMatchUnlock} disabled={unlockBusy}
                className="shrink-0 h-12 px-3 rounded-xl font-semibold text-[12px] text-white/85 flex flex-col items-center justify-center leading-tight disabled:opacity-60"
                style={{ border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.05)" }}>
                {unlockBusy ? (
                  <span>{t("paywall.redirecting")}</span>
                ) : (
                  <>
                    <span>{t("matchsheet.sticky_unlock")}</span>
                    <span className="font-numeral font-bold text-gold">{isNative() && platform() === "ios" ? (appleUnlockPrice || "Chargement…") : `${(trial?.match_unlock_price ?? 1.99).toFixed(2).replace(".", ",")}€`}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Explain modal */}
      <Dialog open={explain} onOpenChange={setExplain}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle className="gold-text font-display">{t("matchsheet.explain_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-white/70 leading-relaxed">
            {t("matchsheet.explain_desc")}
          </p>
          <div className="space-y-2 mt-2">
            {[[t("matchsheet.w_form"), "30%"], [t("matchsheet.w_attack"), "20%"], [t("matchsheet.w_xg"), "15%"],
              [t("matchsheet.w_defense"), "15%"], [t("matchsheet.w_clean"), "10%"], [t("matchsheet.w_possession"), "10%"]].map(([l, w]) => (
              <div key={l} className="flex justify-between text-sm">
                <span className="text-white/60">{l}</span><span className="font-numeral font-bold text-gold">{w}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            {[["0-39", "#E53935"], ["40-59", "#FB8C00"], ["60-79", "#43A047"], ["80-100", "#00E676"]].map(([l, c]) => (
              <div key={l} className="flex-1 text-center">
                <div className="h-1.5 rounded-full" style={{ background: c }} />
                <span className="text-[9px] text-white/50 mt-1 block">{l}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {share && <ShareCard open={!!share} onOpenChange={(v) => !v && setShare(null)} {...share} />}
    </div>
  );
}
